import { isDev } from '@/core/config/env'

import type { HttpMethod } from './core'

const MAX_LOG_STRING_LENGTH = 10_000
const MAX_LOG_COLLECTION_ENTRIES = 50
const MAX_LOG_DEPTH = 4
const REDACTED_VALUE = '[REDACTED]'
const TRUNCATED_VALUE = '[TRUNCATED]'

type ConsoleLogger = Pick<Console, 'groupCollapsed' | 'groupEnd' | 'info'>

export type DevFetchLogEntry = {
  readonly apiUrl: string
  readonly attempt: number
  readonly body?: string | FormData
  readonly context: 'server' | 'client'
  readonly elapsedMs: number
  readonly error?: unknown
  readonly failure?: {
    readonly category: string
    readonly detail: string
    readonly retryable: boolean
    readonly unknownOutcome: boolean
  }
  readonly headers: Readonly<Record<string, string>>
  readonly method: HttpMethod
  readonly response?: Response
  readonly responseBody: unknown
  readonly responseStatus: number
}

export function logDevFetchAttempt(entry: DevFetchLogEntry): void {
  createDevFetchLogger(isDev, console)(entry)
}

export function createDevFetchLogger(
  isDevelopment: boolean,
  logger: ConsoleLogger
): (entry: DevFetchLogEntry) => void {
  return (entry) => {
    if (!isDevelopment) return

    try {
      writeDevFetchLog(entry, logger)
    } catch {
      // 개발용 로그 실패는 요청 흐름이나 재시도 정책에 영향을 주지 않는다.
    }
  }
}

export function writeDevFetchLog(
  entry: DevFetchLogEntry,
  logger: ConsoleLogger
): void {
  const responseHeaders = toHeaderRecord(entry.response?.headers)
  const status = entry.response?.status ?? entry.responseStatus
  const statusLabel = status === 0 ? 'ERR' : String(status)
  const elapsed = `${entry.elapsedMs}ms`

  logger.groupCollapsed(
    `[ Fetch: ${redactUrl(entry.apiUrl)} ] ${entry.method} ${statusLabel} ${elapsed} ${entry.context} attempt=${entry.attempt}`
  )
  try {
    writeLogGroup(logger, 'request', () => {
      logger.info('headers', redactHeaders(entry.headers))
      logger.info('body', snapshotRequestBody(entry.body))
    })
    writeLogGroup(logger, 'response', () => {
      logger.info('headers', redactHeaders(responseHeaders))
      logger.info('body', snapshotValue(entry.responseBody))
      logger.info('metadata', {
        context: entry.context,
        method: entry.method,
        attempt: entry.attempt,
        status,
        outcomeStatus: entry.responseStatus,
        statusText: entry.response?.statusText ?? '',
        elapsedMs: entry.elapsedMs,
        ...(entry.failure === undefined ? {} : { failure: entry.failure }),
        ...(entry.error === undefined
          ? {}
          : { error: snapshotValue(describeError(entry.error)) }),
      })
    })
  } finally {
    logger.groupEnd()
  }
}

function writeLogGroup(
  logger: ConsoleLogger,
  label: string,
  write: () => void
): void {
  logger.groupCollapsed(label)
  try {
    write()
  } finally {
    logger.groupEnd()
  }
}

function redactUrl(value: string): string {
  try {
    const baseUrl =
      typeof window === 'undefined'
        ? 'https://fetch-log.local'
        : window.location.origin
    const url = new URL(value, baseUrl)
    for (const key of new Set(url.searchParams.keys())) {
      if (isSecretKey(key)) url.searchParams.set(key, REDACTED_VALUE)
    }

    if (url.username) url.username = REDACTED_VALUE
    if (url.password) url.password = REDACTED_VALUE
    return url.toString()
  } catch {
    return value
  }
}

function redactHeaders(
  headers: Readonly<Record<string, string>>
): Record<string, string> {
  return Object.fromEntries(
    Object.entries(headers).map(([key, value]) => [
      key,
      isSecretKey(key) ? REDACTED_VALUE : truncateString(value),
    ])
  )
}

function snapshotRequestBody(body: string | FormData | undefined): unknown {
  if (body === undefined) return null
  if (typeof body !== 'string') {
    return snapshotFormData(body)
  }

  try {
    return snapshotValue(JSON.parse(body) as unknown)
  } catch {
    return truncateString(body)
  }
}

function snapshotFormData(formData: FormData): Record<string, unknown> {
  const entries: Record<string, unknown> = {}
  let count = 0

  try {
    for (const [key, value] of formData.entries()) {
      if (count >= MAX_LOG_COLLECTION_ENTRIES) {
        entries[TRUNCATED_VALUE] = TRUNCATED_VALUE
        break
      }
      let snapshot: unknown
      if (isSecretKey(key)) {
        snapshot = REDACTED_VALUE
      } else if (typeof value === 'string') {
        snapshot = truncateString(value)
      } else {
        snapshot = snapshotFile(value)
      }
      appendFormDataValue(entries, key, snapshot)
      count += 1
    }
  } catch {
    return { error: 'FORM_DATA_SNAPSHOT_FAILED' }
  }

  return entries
}

function appendFormDataValue(
  entries: Record<string, unknown>,
  key: string,
  value: unknown
): void {
  const previous = entries[key]
  if (previous === undefined) {
    entries[key] = value
  } else if (Array.isArray(previous)) {
    previous.push(value)
  } else {
    entries[key] = [previous, value]
  }
}

function snapshotFile(value: File): Record<string, unknown> {
  return {
    name: truncateString(value.name),
    type: truncateString(value.type),
    size: value.size,
  }
}

function snapshotValue(
  value: unknown,
  depth = 0,
  seen = new WeakSet<object>()
): unknown {
  if (value === null || value === undefined) return value
  if (typeof value === 'string') return truncateString(value)
  if (
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    typeof value === 'bigint'
  ) {
    return value
  }
  if (typeof value !== 'object') return String(value)
  if (depth >= MAX_LOG_DEPTH) return TRUNCATED_VALUE
  if (seen.has(value)) return '[CIRCULAR]'

  seen.add(value)
  if (Array.isArray(value)) {
    return value
      .slice(0, MAX_LOG_COLLECTION_ENTRIES)
      .map((item) => snapshotValue(item, depth + 1, seen))
  }

  const snapshot: Record<string, unknown> = {}
  try {
    for (const [index, [key, item]] of Object.entries(value).entries()) {
      if (index >= MAX_LOG_COLLECTION_ENTRIES) {
        snapshot[TRUNCATED_VALUE] = TRUNCATED_VALUE
        break
      }
      snapshot[key] = isSecretKey(key)
        ? REDACTED_VALUE
        : snapshotValue(item, depth + 1, seen)
    }
  } catch {
    return '[UNSERIALIZABLE]'
  }

  return snapshot
}

function toHeaderRecord(headers: Headers | undefined): Record<string, string> {
  const record: Record<string, string> = {}
  headers?.forEach((value, key) => {
    record[key] = value
  })
  return record
}

function describeError(error: unknown): string {
  return error instanceof Error ? error.message : String(error)
}

function truncateString(value: string): string {
  return value.length <= MAX_LOG_STRING_LENGTH
    ? value
    : `${value.slice(0, MAX_LOG_STRING_LENGTH)}${TRUNCATED_VALUE}`
}

function isSecretKey(key: string): boolean {
  return /authorization|cookie|csrf|api[-_]?key|token|password|secret/i.test(
    key
  )
}
